// src/shared/auth/session.tsx
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from '../../lib/http/bffClient';

/* ---------- types ---------- */
type NavItem = { path: string; label: string; ability?: string };
type SystemTab = { key: string; label: string };

type MeResponse = {
  user?: any | null;
  roles?: string[];
  perms?: string[];          // abilities
  nav?: NavItem[];           // server-driven sidebar
  systemTabs?: SystemTab[];  // ← NEW: server-driven System Management tabs
  exp?: number;
};

/* ---------- helpers ---------- */

/** Ensure we use a RELATIVE path that CRA will proxy (e.g. '/bff/auth/me'). */
function toRelative(path: string) {
  if (/^https?:\/\//i.test(path)) {
    throw new Error('Use relative /bff/* paths with CRA proxy (Option A).');
  }
  return path.startsWith('/') ? path : `/${path}`;
}

async function bff<T = any>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const url = toRelative(path);
  const headers = new Headers(init.headers || {});
  const method = (init.method || 'GET').toUpperCase();

  // Add CSRF for non-safe methods. Cookie is set by /bff/auth/login or /bff/auth/me.
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = getCookie('csrf');
    if (csrf && !headers.has('X-CSRF')) headers.set('X-CSRF', csrf);
  }
  if (!headers.has('accept')) headers.set('accept', 'application/json');

  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    method,
    headers,
  });

  const contentType = res.headers.get('content-type') || '';
  const text = await res.text().catch(() => '');

  const tryJson = () => {
    try { return text ? JSON.parse(text) : undefined; }
    catch { return undefined; }
  };
  const asJson = contentType.includes('application/json') ? tryJson() : undefined;

  if (!res.ok) {
    const body = asJson ?? text;
    const msg =
      (body && typeof body === 'object' && ('detail' in body || 'error' in body || 'message' in body))
        ? String((body as any).detail ?? (body as any).error ?? (body as any).message)
        : `${method} ${url} → ${res.status} ${res.statusText}`;
    const err: any = new Error(msg);
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return (asJson as T) as T;
}

/* ---------- context ---------- */
type AuthCtx = {
  loading: boolean;
  error: string | null;
  user: any | null;
  roles: string[];
  perms: Set<string>;
  nav: NavItem[];
  systemTabs: SystemTab[];             // ← NEW: exposed to the app
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  can: (ability?: string) => boolean;
};

const Ctx = React.createContext<AuthCtx | null>(null);

// immutable defaults
const DEFAULT_MENU: NavItem[] = [];
const DEFAULT_TABS: SystemTab[] = [];
Object.freeze(DEFAULT_MENU);
Object.freeze(DEFAULT_TABS);

export function AuthProvider({
  children,
  fallbackMenu = DEFAULT_MENU,
  fallbackSystemTabs = DEFAULT_TABS,
}: {
  children: React.ReactNode;
  fallbackMenu?: NavItem[];
  fallbackSystemTabs?: SystemTab[];
}) {
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState<string | null>(null);
  const [user,    setUser]    = React.useState<any | null>(null);
  const [roles,   setRoles]   = React.useState<string[]>([]);
  const [perms,   setPerms]   = React.useState<Set<string>>(new Set());
  const [nav,     setNav]     = React.useState<NavItem[]>(fallbackMenu);
  const [systemTabs, setSystemTabs] = React.useState<SystemTab[]>(fallbackSystemTabs);
  const queryClient = useQueryClient();

  // keep stable refs for fallbacks
  const menuRef = React.useRef<NavItem[]>(fallbackMenu);
  React.useEffect(() => { menuRef.current = fallbackMenu; }, [fallbackMenu]);

  const tabsRef = React.useRef<SystemTab[]>(fallbackSystemTabs);
  React.useEffect(() => { tabsRef.current = fallbackSystemTabs; }, [fallbackSystemTabs]);

  // StrictMode double-invoke guard + de-dupe + cooldown
  const didRunRef   = React.useRef(false);
  const inFlightRef = React.useRef<Promise<void> | null>(null);
  const lastHitRef  = React.useRef(0);
  const COOLDOWN_MS = 3000;

  const coalesce = <T,>(...cands: T[]): T | undefined =>
    cands.find(v => v !== undefined) as T | undefined;

  const refresh = React.useCallback(async () => {
    if (inFlightRef.current) return inFlightRef.current;
    const now = Date.now();
    if (now - lastHitRef.current < COOLDOWN_MS) return;

    setLoading(true); setError(null);
    const p = (async () => {
      try {
        const raw = await bff<unknown>('/bff/auth/me');
        // tolerate shapes: {..}, {data:{..}}, {result:{..}}
        const m = (raw as any)?.data ?? (raw as any)?.result ?? raw as MeResponse;

        setUser(m?.user ?? null);
        setRoles(Array.isArray(m?.roles) ? m!.roles! : []);
        setPerms(new Set(Array.isArray(m?.perms) ? m!.perms! : []));

  // nav: if server returns an empty array treat it as "no override" and fall back to static menu
  const serverNav = Array.isArray((m as any)?.nav) ? (m as any).nav as NavItem[] : undefined;
  const nextNav = serverNav && serverNav.length > 0 ? serverNav : menuRef.current;
  setNav(nextNav);

        const nextTabs =
          (Array.isArray((m as any)?.systemTabs) ? (m as any).systemTabs : undefined) ??
          (Array.isArray((m as any)?.system_tabs) ? (m as any).system_tabs : undefined) ??
          tabsRef.current;
        setSystemTabs(nextTabs);
      } catch (e: any) {
        // If unauthenticated and we are on a public media path, attempt auto-guest
        const status = e?.status ?? (typeof e?.message === 'string' && /401/.test(e.message) ? 401 : undefined);
        const path = (typeof window !== 'undefined' ? window.location.pathname : '') || '';
        const isMediaPublic = path.startsWith('/media');
        if (status === 401 && isMediaPublic) {
          try {
            await bff('/bff/auth/guest', { method: 'POST' });
            // Retry me after seeding guest session
            const raw2 = await bff<unknown>('/bff/auth/me');
            const m2 = (raw2 as any)?.data ?? (raw2 as any)?.result ?? raw2 as MeResponse;

            setUser(m2?.user ?? null);
            setRoles(Array.isArray(m2?.roles) ? m2!.roles! : []);
            setPerms(new Set(Array.isArray(m2?.perms) ? m2!.perms! : []));

            const serverNav2 = Array.isArray((m2 as any)?.nav) ? (m2 as any).nav as NavItem[] : undefined;
            const nextNav2 = serverNav2 && serverNav2.length > 0 ? serverNav2 : menuRef.current;
            setNav(nextNav2);

            const nextTabs2 =
              (Array.isArray((m2 as any)?.systemTabs) ? (m2 as any).systemTabs : undefined) ||
              (Array.isArray((m2 as any)?.system_tabs) ? (m2 as any).system_tabs : undefined) ||
              tabsRef.current;
            setSystemTabs(nextTabs2);
            setError(null);
          } catch (e2: any) {
            // fall back to unauthenticated state
            setUser(null);
            setRoles([]);
            setPerms(new Set());
            setNav(menuRef.current);
            setSystemTabs(tabsRef.current);
            setError(e2?.message || 'Not authenticated');
          }
        } else {
          setUser(null);
          setRoles([]);
          setPerms(new Set());
          setNav(menuRef.current);
          setSystemTabs(tabsRef.current);
          setError(e?.message || 'Not authenticated');
        }
      } finally {
        lastHitRef.current = Date.now();
        setLoading(false);
      }
    })().finally(() => { inFlightRef.current = null; });

    inFlightRef.current = p;
    return p;
  }, []);

  const logout = React.useCallback(async () => {
    try { await bff('/bff/auth/logout', { method: 'POST' }); } catch {}
    setUser(null);
    setRoles([]);
    setPerms(new Set());
    setNav(menuRef.current);
    setSystemTabs(tabsRef.current);
  // Invalidate any cached auth/me data so guarded routes re-check
  try { queryClient.removeQueries({ queryKey: ['me'] }); } catch {}
  }, []);

  React.useEffect(() => {
    if (didRunRef.current) return; // avoid StrictMode double effect
    didRunRef.current = true;
    void refresh();
  }, [refresh]);

  const can = React.useCallback((ability?: string) => {
    if (!ability) return true;
    return perms.has(ability);
  }, [perms]);

  const value: AuthCtx = {
    loading,
    error,
    user,
    roles,
    perms,
    nav,
    systemTabs,     // ← exposed in context
    refresh,
    logout,
    can,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useCan() {
  return useAuth().can;
}
